import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, Check, History, Upload, UserRound, X } from "lucide-react";
import { requestPlayerPhoto, errorMessage } from "@/lib/api";
import { invalidateScope } from "@/lib/queryClient";
import type { LinkedAthlete, Player, PlayerPhotoRequest } from "@/lib/types";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/components/ui/toast";
import { PlayerAvatar } from "@/features/player-photos/PlayerAvatar";

export type LinkedPlayersStripProps = {
  players: Player[];
  photoRequests?: PlayerPhotoRequest[];
  selectedPlayerId?: string;
  onSelect?: (id: string) => void;
  /** #908 P3d — the account's linked athletes (`GET /api/me/athletes`).
   * When present the strip is an ATHLETE picker: one chip per athlete,
   * selection resolves to that athlete's roster row in the active scope
   * (`players`) or opens the all-seasons view; `players` alone keeps the
   * legacy per-scope chips. */
  athletes?: LinkedAthlete[];
  selectedAthleteId?: string;
  onSelectAthlete?: (athlete: LinkedAthlete) => void;
  /** The all-seasons collection is open for the selected athlete. */
  allSeasons?: boolean;
  onToggleAllSeasons?: () => void;
};

const RELATIONSHIP_LABELS: Record<LinkedAthlete["relationship"], string> = {
  self: "You",
  guardian: "Guardian",
  family: "Family",
};

type AvatarStyle = "neutral" | "female" | "male";

const AVATAR_STYLES: readonly [AvatarStyle, string][] = [
  ["neutral", "Neutral"],
  ["female", "Female"],
  ["male", "Male"],
];

export function LinkedPlayersStrip({
  players,
  photoRequests = [],
  selectedPlayerId,
  onSelect,
  athletes = [],
  selectedAthleteId,
  onSelectAthlete,
  allSeasons = false,
  onToggleAllSeasons,
}: LinkedPlayersStripProps) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [activePlayer, setActivePlayer] = useState<Player | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [avatarStyle, setAvatarStyle] = useState<AvatarStyle | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const latestRequestByPlayer = useMemo(() => {
    const map = new Map<string, PlayerPhotoRequest>();
    for (const request of photoRequests) {
      if (!map.has(request.player_id)) map.set(request.player_id, request);
    }
    return map;
  }, [photoRequests]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!activePlayer) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setActivePlayer(null);
      setFile(null);
      setAvatarStyle(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activePlayer]);

  if (players.length === 0 && athletes.length === 0) {
    return (
      <div className="family-linked-empty" data-testid="feedback-linked-empty">
        <UserRound size={22} aria-hidden />
        <span>
          <strong>No roster player linked yet.</strong>
          <small>Ask your coach to link your account to a player.</small>
        </span>
      </div>
    );
  }

  const reset = () => {
    setActivePlayer(null);
    setFile(null);
    setAvatarStyle(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async () => {
    if (!activePlayer || (!file && !avatarStyle)) return;
    setSubmitting(true);
    try {
      await requestPlayerPhoto(
        activePlayer.id,
        file ? { file } : { avatarStyle: avatarStyle! },
      );
      toast.success("Appearance request sent");
      await invalidateScope();
      reset();
    } catch (err) {
      toast.error("Couldn't send photo request", {
        description: errorMessage(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const selected = players.find((p) => p.id === selectedPlayerId);
  const selectedPending =
    (selected ? latestRequestByPlayer.get(selected.id)?.status : null) === "pending";

  // One chip per linked athlete. The avatar resolves through the athlete's
  // roster row in the active scope when it has one (so the photo + jersey
  // match the per-scope chip), else through its first assignment.
  const renderAthleteChip = (linked: LinkedAthlete) => {
    const assignments = linked.assignments ?? [];
    const scoped = assignments.find((a) => players.some((p) => p.id === a.player_id));
    const assignment = scoped ?? assignments[0];
    const player = players.find((p) => p.id === scoped?.player_id);
    const athleteId = linked.athlete.id;
    const isSelected = selectedAthleteId === athleteId;
    const avatarPlayer: Player = player ?? ({
      id: assignment?.player_id ?? athleteId,
      display_name: linked.athlete.display_name,
      photo_filename: linked.athlete.photo_filename ?? null,
      avatar_style: "neutral",
      jersey_number: assignment?.jersey_number ?? "",
      position: assignment?.position ?? "",
      athlete_id: athleteId,
    } as Player);
    const latest = player ? latestRequestByPlayer.get(player.id) : undefined;
    const pending = latest?.status === "pending";
    const profile = (
      <>
        <PlayerAvatar player={avatarPlayer} size="sm" />
        <span className="family-player-copy min-w-0">
          <strong className="block">{linked.athlete.display_name}</strong>
          <small className="block">
            {[
              assignment?.jersey_number ? `#${assignment.jersey_number}` : null,
              assignment?.position || null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </small>
        </span>
        <Badge variant="muted">{RELATIONSHIP_LABELS[linked.relationship]}</Badge>
        {athletes.length > 1 && onSelectAthlete ? (
          <Check className="family-player-check" size={16} aria-hidden />
        ) : null}
      </>
    );
    return (
      <span
        key={athleteId}
        data-feedback-linked-athlete={athleteId}
        data-feedback-linked-player={player?.id}
        data-selected={isSelected}
        data-profile={!onSelectAthlete}
        className="inline-flex items-center gap-2 rounded border border-rule bg-surface px-2 py-1 text-xs text-ink"
      >
        {onSelectAthlete ? (
          <button
            type="button"
            aria-pressed={isSelected}
            onClick={() => onSelectAthlete(linked)}
            className="family-player-choice"
          >
            {profile}
          </button>
        ) : (
          <div className="family-player-choice">{profile}</div>
        )}
        {pending ? (
          <Badge variant="accent">Photo pending</Badge>
        ) : latest?.status === "rejected" ? (
          <Badge variant="muted">Photo not approved</Badge>
        ) : null}
      </span>
    );
  };

  return (
    <>
      <div className="family-linked-players" data-testid="feedback-linked-strip">
        {selected && <button
          type="button"
          className="family-player-photo"
          aria-label="Request photo change"
          title={`Request photo change for ${selected.display_name}`}
          aria-description={`For ${selected.display_name}${selectedPending ? ' · photo request pending' : ''}`}
          disabled={selectedPending}
          onClick={() => {
            setActivePlayer(selected);
            setFile(null);
            setAvatarStyle(null);
          }}
        >
          <Camera size={20} aria-hidden />
        </button>}
        <div className="family-player-choices">
          {athletes.length > 0 ? athletes.map(renderAthleteChip) : players.map((p) => {
          const latest = latestRequestByPlayer.get(p.id);
          const pending = latest?.status === "pending";
          const profile = (
            <>
              <PlayerAvatar player={p} size="sm" />
              <span className="family-player-copy min-w-0">
                <strong className="block">{p.display_name}</strong>
                <small className="block">
                  {[
                    p.jersey_number ? `#${p.jersey_number}` : null,
                    p.position,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </small>
              </span>
              {players.length > 1 && onSelect ? (
                <Check
                  className="family-player-check"
                  size={16}
                  aria-hidden
                />
              ) : null}
            </>
          );
          return (
            <span
              key={p.id}
              data-feedback-linked-player={p.id}
              data-selected={selectedPlayerId === p.id}
              data-profile={!onSelect}
              className="inline-flex items-center gap-2 rounded border border-rule bg-surface px-2 py-1 text-xs text-ink"
            >
              {onSelect ? (
                <button
                  type="button"
                  aria-pressed={selectedPlayerId === p.id}
                  onClick={() => onSelect(p.id)}
                  className="family-player-choice"
                >
                  {profile}
                </button>
              ) : (
                <div className="family-player-choice">{profile}</div>
              )}
              {pending ? (
                <Badge variant="accent">Photo pending</Badge>
              ) : latest?.status === "rejected" ? (
                <Badge variant="muted">Photo not approved</Badge>
              ) : null}
            </span>
          );
          })}
        </div>
        {onToggleAllSeasons ? (
          <Button
            type="button"
            variant={allSeasons ? "primary" : "outline"}
            size="sm"
            className="min-h-11 self-center"
            aria-pressed={allSeasons}
            data-testid="feedback-all-seasons"
            onClick={onToggleAllSeasons}
          >
            <History size={16} aria-hidden />
            All seasons
          </Button>
        ) : null}
      </div>

      {activePlayer && typeof document !== "undefined"
        ? createPortal(
            <div
              className="family-photo-dialog-backdrop"
              role="dialog"
              aria-modal="true"
              aria-labelledby="family-photo-dialog-title"
              data-testid="feedback-photo-dialog"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) reset();
              }}
            >
              <section className="family-photo-dialog">
                <header>
                  <h2 id="family-photo-dialog-title">
                    Request a player photo change
                  </h2>
                  <button
                    type="button"
                    onClick={reset}
                    aria-label="Close dialog"
                  >
                    <X size={20} aria-hidden />
                  </button>
                </header>
                <div className="family-photo-dialog-body">
                  <div className="family-photo-player">
                    <div className="family-photo-preview">
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <PlayerAvatar
                          player={{
                            ...activePlayer,
                            avatar_style:
                              avatarStyle ?? activePlayer.avatar_style,
                            photo_filename: avatarStyle
                              ? null
                              : activePlayer.photo_filename,
                          }}
                          size="lg"
                        />
                      )}
                    </div>
                    <span>
                      <strong>{activePlayer.display_name}</strong>
                      <small>
                        {[
                          activePlayer.jersey_number
                            ? `#${activePlayer.jersey_number}`
                            : null,
                          activePlayer.position,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </small>
                    </span>
                  </div>
                  <p>
                    Your coach reviews photo and avatar changes before they
                    appear on your player profile.
                  </p>
                  <label className="family-photo-file-label">
                    <span>Choose a photo</span>
                    <span className="family-photo-file-control">
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload size={16} strokeWidth={2} aria-hidden="true" />
                        Choose file
                      </Button>
                      <span>{file?.name ?? "No file chosen"}</span>
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                      className="sr-only"
                      onChange={(e) => {
                        const selectedFile = e.currentTarget.files?.[0] ?? null;
                        setFile(selectedFile);
                        if (selectedFile) setAvatarStyle(null);
                      }}
                    />
                  </label>
                  <p className="family-photo-hint">
                    PNG, JPG, JPEG or WebP. A coach reviews the request before
                    the photo is saved.
                  </p>
                  <section className="family-avatar-picker">
                    <h3>Or choose a generic avatar</h3>
                    <div role="group" aria-label="Generic avatar">
                      {AVATAR_STYLES.map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          className="player-avatar-choice"
                          aria-pressed={avatarStyle === value}
                          data-testid={`feedback-avatar-${value}`}
                          onClick={() => {
                            setAvatarStyle(value);
                            setFile(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                        >
                          <PlayerAvatar
                            player={{
                              ...activePlayer,
                              photo_filename: null,
                              avatar_style: value,
                            }}
                            size="md"
                          />
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                    <p className="family-photo-hint">
                      Choosing an avatar replaces the current photo after your
                      coach approves it.
                    </p>
                  </section>
                </div>
                <footer>
                  <Button
                    type="button"
                    disabled={(!file && !avatarStyle) || submitting}
                    onClick={submit}
                  >
                    Send request
                  </Button>
                  <Button type="button" variant="ghost" onClick={reset}>
                    Cancel
                  </Button>
                </footer>
              </section>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
